import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { SxProps, Theme } from '@mui/material/styles';
import { TextFieldProps } from '@mui/material/TextField';
import {
  DateRange,
  DateRangePicker,
  PickersShortcutsItem,
  PickersShortcutsProps,
  SingleInputDateRangeField,
  useIsValidValue,
  usePickerActionsContext,
} from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { get } from 'lodash-es';
import { useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldValues,
} from 'react-hook-form';

import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';

interface SingleInputDateRangePickerProps<TFieldValues extends FieldValues> {
  InputProps?: TextFieldProps['InputProps'];
  control: Control<TFieldValues>;
  customShortcuts?: PickersShortcutsItem<DateRange<Dayjs>>[];
  defaultOpen?: boolean;
  disabled?: boolean;
  errors: FieldErrors<TFieldValues>;
  id?: string;
  inputProps?: TextFieldProps['inputProps'];
  isFullWidth?: boolean;
  isRequired?: boolean;
  label?: string;
  maxDate?: Dayjs;
  minDate?: Dayjs;
  multiline?: boolean;
  name: FieldPath<TFieldValues>;
  placeholder?: string;
  rows?: number;
  sx?: SxProps<Theme>;
  type?: string;
}

const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
  {
    label: t('date.yesterday'),
    getValue: () => {
      const today = dayjs().tz();
      const yesterday = today.subtract(1, 'day');
      return [yesterday.startOf('day'), yesterday.endOf('day')];
    },
  },
  {
    label: t('date.today'),
    getValue: () => {
      const today = dayjs().tz();
      return [today.startOf('day'), today.endOf('day')];
    },
  },
  {
    label: t('date.this_week'),
    getValue: () => {
      const today = dayjs().tz();
      return [today.startOf('week'), today.endOf('week')];
    },
  },
  {
    label: t('date.last_week'),
    getValue: () => {
      const today = dayjs().tz();
      const prevWeek = today.subtract(7, 'day');
      return [prevWeek.startOf('week'), prevWeek.endOf('week')];
    },
  },
  {
    label: t('date.last_seven_days'),
    getValue: () => {
      const today = dayjs().tz();
      return [today.subtract(7, 'day'), today];
    },
  },
  {
    label: t('date.current_month'),
    getValue: () => {
      const today = dayjs().tz();
      return [today.startOf('month'), today.endOf('month')];
    },
  },

  { label: t('actions.reset'), getValue: () => [null, null] },
];
const CustomRangeShortcuts = (props: PickersShortcutsProps<DateRange<Dayjs>>) => {
  const { items, changeImportance = 'accept' } = props;
  const { setValue } = usePickerActionsContext<DateRange<Dayjs>>();
  const isValid = useIsValidValue<DateRange<Dayjs>>();

  if (!items || items?.length === 0) {
    return null;
  }

  const resolvedItems = items.map((item) => {
    const { getValue, ...shortcut } = item;
    const newValue = getValue({ isValid });

    return {
      label: shortcut.label,
      onClick: () => {
        setValue(newValue, { changeImportance, shortcut, source: 'view' });
      },
      disabled: !isValid(newValue),
    };
  });

  return (
    <Box
      sx={{
        gridRow: 1,
        gridColumn: 2,
      }}
    >
      <List
        dense
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          '& .MuiListItem-root': {
            pt: 0.5,
            pl: 0,
            pr: 1,
            justifyContent: 'flex-start',
            '&:last-child': {
              ml: 2,
              mr: -1,
            },
          },
        }}
      >
        {resolvedItems.map((item, index) => {
          return (
            <ListItem
              key={item.label}
              sx={{
                justifySelf: index === resolvedItems?.length - 1 ? 'end' : 'end',
              }}
            >
              <Chip
                {...item}
                color={index === resolvedItems?.length - 1 ? 'default' : 'primary'}
              />
            </ListItem>
          );
        })}
      </List>
      <Divider />
    </Box>
  );
};
const parseDateInput = (dates: DateRange<Dayjs> | null): DateRange<Dayjs> => {
  if (!dates) {
    return [null, null];
  }

  const endDate = dates[1] ? dayjs.tz(dates[1]).endOf('day') : dates[1];
  return [dates[0], endDate];
};

const toDateRangeValue = (value: unknown): DateRange<Dayjs> => {
  if (!Array.isArray(value)) {
    return [null, null];
  }

  const [startDate, endDate] = value;
  return [startDate ? dayjs.tz(startDate) : null, endDate ? dayjs.tz(endDate) : null];
};

function SingleInputDateRangePicker<TFieldValues extends FieldValues>({
  InputProps,
  control,
  defaultOpen,
  errors,
  isFullWidth,
  isRequired,
  label,
  name,
  sx,
  minDate,
  maxDate,
  customShortcuts,
}: SingleInputDateRangePickerProps<TFieldValues>) {
  const [open, setOpen] = useState<boolean>(defaultOpen || false);
  const [draftRange, setDraftRange] = useState<DateRange<Dayjs> | null>(null);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => {
        const fieldError = get(errors, name);
        const errorMessage = get(errors, `${name}.message`);
        const helperText = typeof errorMessage === 'string' ? errorMessage : '';

        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              ...sx,
            }}
          >
            {label && <FormFieldLabel label={`${label}${isRequired ? ' *' : ''}`} />}
            <DateRangePicker
              enableAccessibleFieldDOMStructure={false}
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => {
                setOpen(false);
                setDraftRange(null);
              }}
              slots={{
                field: SingleInputDateRangeField,
                shortcuts: CustomRangeShortcuts,
              }}
              onChange={setDraftRange}
              onAccept={(newValues) => {
                // MUI treats click-away and Escape as accept, so a dismissal mid-selection
                // would commit a half-picked range to the form
                const [startDate, endDate] = newValues;
                const selectionIsPartial = Boolean(startDate) !== Boolean(endDate);
                if (!selectionIsPartial) {
                  onChange(parseDateInput(newValues));
                }
                setDraftRange(null);
              }}
              value={draftRange ?? toDateRangeValue(value)}
              slotProps={{
                shortcuts: {
                  items: customShortcuts || shortcutsItems,
                },
                textField: {
                  fullWidth: isFullWidth,
                  required: isRequired,
                  variant: 'outlined',
                  sx: { ...sx },
                  className: isRequired ? 'required' : '',
                  error: Boolean(fieldError),
                  helperText,
                  slotProps: {
                    input: {
                      sx: { fontSize: '12px', py: '3px', ...InputProps?.sx },
                    },
                  },
                },
              }}
              minDate={minDate}
              maxDate={maxDate}
            />
          </Box>
        );
      }}
    />
  );
}

export { SingleInputDateRangePicker };
